import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import PagedSearch from 'components/PagedSearch'
import Pagination from 'components/Pagination'
import Table from 'components/Table'

import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import useDealers from 'hooks/useDealers'
import useSearchParams from 'hooks/useSearchParams'

import { fullOneLineAddress } from 'helpers/formatters'

import Api from 'services/api'

import { screenSize, LoadingSinglePanePage, PageTitle, SinglePanePage } from 'styles/shared'

const Dealers = () => {
  const { session, setSession } = useSessionContext()
  const queryFromUrl = useSearchParams('q')
  const pageFromUrl = parseInt(useSearchParams('page')) || 1

  const { dealers, dealersMetadata, areDealersLoaded } = useDealers({ query: queryFromUrl, page: pageFromUrl })
  const [requestedImpersonateDealerId, setRequestedImpersonateDealerId] = useState(null)
  const [showSpinner, setShowSpinner] = useState(false)
  const { resetDealerScopedData } = useStoreContext()
  const navigate = useNavigate()

  const constructUrl = ({ page = pageFromUrl, query = queryFromUrl }) => (
    `/admin/dealers?page=${page}${query ? `&q=${query}` : ''}`
  )

  const handlePageChange = (p) => {
    navigate(constructUrl({ page: p }))
  }

  const handleImpersonate = useCallback(async (dealerId) => {
    setRequestedImpersonateDealerId(dealerId)

    await Api.Session.update({ impersonateDealerId: dealerId })
      .then((res) => {
        resetDealerScopedData()
        setSession(res)
        navigate('/customers')
      })
  }, [navigate, resetDealerScopedData, setSession])

  const onSearch = (newQuery = '') => {
    setShowSpinner(true)
    setTimeout(() => {
      navigate(constructUrl({ query: newQuery }))
      setShowSpinner(false)
    })
  }

  if (!areDealersLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <SinglePanePage>
      <S.Top>
        <S.Header>
          <PageTitle>{dealersMetadata?.filteredItemCount} Dealer{dealersMetadata?.filteredItemCount === 1 ? '' : 's'}</PageTitle>

          <div>
            <S.PagedSearch query={queryFromUrl} onSearch={onSearch} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
            {dealersMetadata?.filteredItemCount > 10 && <Pagination pageCount={dealersMetadata.pageCount} onPageChange={handlePageChange} page={dealersMetadata.pageNumber} />}
          </div>
        </S.Header>
      </S.Top>

      <Table
        items={dealers}
        noDataMessage={{
          title: 'No dealers',
          description: 'Dealers will show up here.'
        }}
      >
        <Table.Header>
          <S.MainHeaderCells title="Name" />
          <S.MainHeaderCells title="Phone Number" />
          <S.MainHeaderCells title="Address" />
          <S.LastHeaderCell title="Last logged in user" />
        </Table.Header>

        {dealers.map(dealer => (
          <div key={dealer.id}>
            <S.TableRow>
              <S.MainCells>{dealer.name}</S.MainCells>
              <S.MainCells>{dealer.phoneNumber}</S.MainCells>
              <S.MainCells>{fullOneLineAddress(dealer.address, dealer.city, dealer.state, dealer.zip)}</S.MainCells>
              <S.LastCell>
                <S.ButtonCell>
                  <span>
                    {dealer.lastLoggedInUser}
                  </span>

                  <S.ButtonContainer>
                    {session.isAdmin && (
                      <>
                        <Button decoration="secondary" text="Transfer" styles={{ button: { marginRight: 15 } }} onPress={() => navigate(`/admin/dealers/${dealer.id}/migrate`)} />
                        <Button decoration="positive" text="Impersonate" enabled={requestedImpersonateDealerId !== dealer.id} onPress={() => handleImpersonate(dealer.id, session.id)} />
                      </>
                    )}
                  </S.ButtonContainer>
                </S.ButtonCell>
              </S.LastCell>
            </S.TableRow>
          </div>
        ))}
      </Table>
      {dealersMetadata?.filteredItemCount > 10 &&
      <S.BottomPaginationContainer>
        <Pagination pageCount={dealersMetadata.pageCount} onPageChange={handlePageChange} page={dealersMetadata.pageNumber} />
      </S.BottomPaginationContainer>
      }
    </SinglePanePage>
  )
}

const LAST_COLUMN_FLEX_VALUE = '2'
const MAIN_COLUMN_FLEX_VALUE = '1'

const CellStyles = `
  line-height: 24px;
  text-overflow: ellipsis;
  padding: 20px 8px;
  overflow: hidden;

  @media ${screenSize.medium} {
    line-height: inherit;
    padding: 10px 8px;
  }
  @media ${screenSize.small} {
    line-height: inherit;
    padding: 10px 5px;
  }
  `

const S = {
  LastHeaderCell: styled(Table.HeaderCell)`
    flex: ${LAST_COLUMN_FLEX_VALUE};
  `,
  MainHeaderCells: styled(Table.HeaderCell)`
    flex: ${MAIN_COLUMN_FLEX_VALUE};
  `,
  TableRow: styled(Table.Row)`
    align-items: center;
  `,
  LastCell: styled(Table.Cell)`
    flex: ${LAST_COLUMN_FLEX_VALUE};
    flex-direction: column;
    margin-right: 15px;

    @media ${screenSize.small} {
      margin-right: 0;
      width: 100%;
    }
  `,
  MainCells: styled(Table.Cell)`
    flex: ${MAIN_COLUMN_FLEX_VALUE};
    flex-direction: column;
    ${CellStyles}

    @media ${screenSize.small} {
      display: inline-block;
      width: 100%;
      padding: 5px;
      line-height: 20px;
    }
  `,
  ButtonContainer: styled.div`
    display: flex;
    gap: 10px;
  `,
  ButtonCell: styled(Table.Cell)`
    ${CellStyles}
    justify-content: space-between;
    align-items: center;

    @media ${screenSize.medium} {
      flex-direction: column;
      -webkit-box-pack: start;
      justify-content: start;
    }
    @media ${screenSize.small} {
      margin: 0;
      flex-direction: row;
      -webkit-box-pack: space-between;
      justify-content: space-between;
    }
  `,
  PagedSearch: styled(PagedSearch)`
    display: inline-block;
    white-space: nowrap;
    margin-bottom: 15px;
  `,
  Header: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,
  Top: styled.div`
    display: flex;
    justify-content: flex-start;
  `,
  BottomPaginationContainer: styled.div`
    padding-top: 16px;
    padding-bottom: 40px;
  `
}

export default Dealers
